import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AttendanceCaptureFields from './AttendanceCaptureFields.tsx';
import { AttendanceContext, AttendanceDecision, AttendanceErrors, attendanceDecision, initialAttendanceValues, selectedAttendanceAppointment } from './attendanceCapture.ts';

type Props = {
    open: boolean; context: AttendanceContext; busy?: boolean; error?: string; uncertain?: boolean; requiresReload?: boolean;
    retryDecision?: AttendanceDecision | null;
    onSubmit: (decision: AttendanceDecision | null) => void | Promise<void>; onCancel: () => void; onReload?: () => void;
};
export default function AttendanceCaptureDialog({ open, context, busy = false, error, uncertain = false, requiresReload = false, retryDecision, onSubmit, onCancel, onReload }: Props) {
    const [value, setValue] = useState(() => initialAttendanceValues(context));
    const [errors, setErrors] = useState<AttendanceErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [localError, setLocalError] = useState('');
    const locked = useRef(false);
    const generation = useRef(0);
    const receivedAt = useRef(performance.now());
    useEffect(() => {
        generation.current += 1;
        receivedAt.current = performance.now();
        locked.current = false;
        setSubmitting(false); setValue(initialAttendanceValues(context)); setErrors({}); setLocalError('');
        return () => { generation.current += 1; };
    }, [context, open]); // A new scope must never retain the previous practice's evidence.
    const selected = selectedAttendanceAppointment(context, value);
    const consumed = value.kind === 'attended' && selected && selected.status !== 'BOOKED' && !selected.can_record_replacement && !(context.direct_attendance && selected.status === 'ATTENDED');
    const busyNow = busy || submitting;
    const submit = async () => {
        if (locked.current || busy || requiresReload || (consumed && !uncertain)) return;
        const { decision, errors: validationErrors } = uncertain && retryDecision !== undefined
            ? { decision: retryDecision, errors: {} } : attendanceDecision(context, value, performance.now() - receivedAt.current);
        setErrors(validationErrors);
        if (!decision && !(uncertain && retryDecision === null)) return;
        locked.current = true; setSubmitting(true); setLocalError('');
        const { current } = generation;
        try { await onSubmit(decision ?? null); }
        catch (failure) {
            if (current === generation.current) setLocalError(failure instanceof Error ? failure.message : 'Attendance could not be confirmed. Please retry.');
        } finally {
            if (current === generation.current) { locked.current = false; setSubmitting(false); }
        }
    };
    return <Dialog open={open} fullWidth maxWidth="sm" onClose={busyNow ? undefined : onCancel} aria-labelledby="attendance-capture-title">
        <DialogTitle id="attendance-capture-title">Record consultation attendance</DialogTitle>
        <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
                {(error || localError) && <Alert severity="error">{error || localError}</Alert>}
                {uncertain && <Alert severity="warning">This confirmation may have saved. Retry the original confirmation to check its result. Closing this dialog does not undo a saved change.</Alert>}
                {uncertain && retryDecision ? <Alert severity="info">{retryDecision.kind === 'not_required'
                    ? `Original confirmation: no consultation required. Reason: ${retryDecision.reason}`
                    : `Original confirmation: ${retryDecision.kind.replaceAll('_', ' ')}${retryDecision.appointment_id ? `, consultation #${retryDecision.appointment_id}` : ''}${retryDecision.occurred_at ? `, ${retryDecision.occurred_at}` : ''}${retryDecision.practitioner_id ? `, ${context.practitioners.find((person) => person.id === retryDecision.practitioner_id)?.name || 'selected dentist'}` : ''}.`}</Alert> : null}
                {uncertain && retryDecision === null && <Alert severity="info">Retry the original treatment status change using its existing attendance evidence.</Alert>}
                {!uncertain && <AttendanceCaptureFields context={context} value={value} disabled={busyNow || requiresReload}
                    errors={errors} onChange={(next) => { setValue(next); setErrors({}); }} />}
            </Stack>
        </DialogContent>
        <DialogActions>
            <Button onClick={onCancel} disabled={busyNow}>{uncertain ? 'Close' : 'Cancel'}</Button>
            {requiresReload ? <Button onClick={onReload} disabled={!onReload || busyNow}>Reload consultation</Button>
                : <LoadingButton variant="contained" loading={busyNow} disabled={Boolean(busy || (!uncertain && consumed) || (uncertain && retryDecision === undefined))} onClick={submit}>{uncertain ? 'Retry original confirmation' : 'Confirm attendance'}</LoadingButton>}
        </DialogActions>
    </Dialog>;
}
